// ----------      START   IMPORT  BLOCK        ----------
import React from "react";
import { Row, Col, Form, Button, Card } from "react-bootstrap";
import { FaPlus, FaTrash, FaPhoneAlt, FaMapMarkerAlt } from "react-icons/fa";
import PhoneWidget from "../../../Generic/PhoneWidget";
import EnumSelect from "./Widgets/EnumSelect";
import ErrorMessage from "../../../Generic/ErrorMessage";
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useLanguage } from "../../../../../API/Context/LanguageContext";
import { RELATIONSHIPS } from "../../../../../ConfigFiles/maps/relationships_map";
import { RelationshipsEnum } from "../../../../../Types/Enums/RelationshipsEnum";
import { VocabProfileOnboarding as Vocab } from "../../../../../ConfigFiles/multilingual/VocabProfileOnboarding";
import SpecialtySubPicker, { type SelectedItem } from "../../../LoggedUserGeneral/ProOnboarding/Steps/Widgets/SpecialtySubPicker";
import StateService from "../../../../../API/Services/Geography/StateService";
// ----------      END   IMPORT  BLOCK        ----------

export interface CurrentAddressState {
  street: string;
  number: string;
  interior_number?: number | string | null;
  state: string | null;
  state_id?: string | null;
  state_name?: string;
  zip_code: string;
  country: string;
  google_maps_url?: string | null;
}

export interface LocalEmergencyContact {
  name: string;
  phone: string;
  relationship: RelationshipsEnum | number;
}

export interface StepContactFormData {
  phone_numbers: string[];
  current_address: CurrentAddressState;
  emergency_contacts: LocalEmergencyContact[];
}

interface StepContactProps {
  data: StepContactFormData;
  update: (newData: Partial<StepContactFormData>) => void;
}

export const StepContact: React.FC<StepContactProps> = ({ data, update }) => {
  const { lan } = useLanguage();
  const statesService = new StateService();

  const isInvalid = (value: any): boolean => {
    if (typeof value === 'string') return value.trim() === "";
    return value === null || value === undefined;
  };

  const handleAddressChange = (field: keyof CurrentAddressState, value: string, isInt = false) => {
    let processedValue: string | number | null = value;
    if (isInt) {
      processedValue = value.replace(/\D/g, "");
      if (processedValue.length > 1 && processedValue.startsWith("0")) {
        processedValue = processedValue.replace(/^0+/, "");
      }
    }
    update({
      current_address: { ...data.current_address, [field]: processedValue }
    });
  };

  const updateArray = <K extends 'phone_numbers' | 'emergency_contacts'>(
    listName: K, 
    index: number, 
    field: string | null, 
    value: any
  ) => {
    const newList = [...data[listName]] as any[];
    if (field) {
      newList[index] = { ...newList[index], [field]: value };
    } else {
      newList[index] = value;
    }
    update({ [listName]: newList });
  };

  const addRow = <K extends 'phone_numbers' | 'emergency_contacts'>(
    listName: K, 
    template: any, 
    limit: number
  ) => {
    if (data[listName].length < limit) {
      update({ [listName]: [...data[listName], template] as any });
    }
  };

  const removeRow = <K extends 'phone_numbers' | 'emergency_contacts'>(
    listName: K, 
    index: number
  ) => {
    if (data[listName].length > 1) {
      update({ [listName]: data[listName].filter((_, i) => i !== index) as any });
    }
  };

  return (
    <div className="animate-in">
      <h4 className="mb-4 border-start border-primary border-4 ps-3">{Vocab.contact_title[lan]}</h4>
      
      {/* --- ADDRESS SECTION --- */}
      <Card className="mb-4 border-0 bg-light p-3 shadow-sm">
        <Form.Label className="fw-bold mb-3">
          <FaMapMarkerAlt className="me-2 text-primary"/>{Vocab.current_address[lan]}
        </Form.Label>
        <Row className="g-2">
          <Col md={8}>
            <Form.Control 
              placeholder={Vocab.street[lan]} 
              value={data.current_address.street || ""} 
              isInvalid={isInvalid(data.current_address.street)} 
              onChange={(e) => handleAddressChange('street', e.target.value)} 
            />
          </Col>
          <Col md={2}>
            <Form.Control 
              type="text" 
              inputMode="numeric" 
              placeholder={Vocab.number[lan]} 
              value={data.current_address.number || ""} 
              isInvalid={isInvalid(data.current_address.number)} 
              onChange={(e) => handleAddressChange('number', e.target.value, true)} 
            />
          </Col>
          <Col md={2}>
            <Form.Control 
              type="text" 
              inputMode="numeric" 
              placeholder={Vocab.interior_number[lan]} 
              value={data.current_address.interior_number ?? ""} 
              onChange={(e) => handleAddressChange('interior_number', e.target.value, true)} 
            />
          </Col>
          <Col md={4}>
            <Form.Control 
              placeholder={Vocab.zip_code[lan]} 
              value={data.current_address.zip_code || ""} 
              isInvalid={isInvalid(data.current_address.zip_code)} 
              onChange={(e) => handleAddressChange('zip_code', e.target.value)} 
            />
          </Col>
          <Col md={4}>
            <Form.Control 
              value={lan === 0 ? "México" : "Mexico"} 
              readOnly 
              className="bg-light text-muted" 
              style={{ cursor: "not-allowed", height: "38px" }} 
            />
          </Col>
          <Col md={4}>
            <div className={isInvalid(data.current_address.state) ? "border border-danger rounded" : ""}>
              <SpecialtySubPicker
                searchFunction={(query: string, signal?: AbortSignal) => statesService.searchState(query, signal)}
                selectedValue={data.current_address.state || ""}
                selectedLabel={data.current_address.state_name || ""}
                placeholder={Vocab.state[lan] ?? "Estado"}
                onSelect={(item: SelectedItem | null) => {
                  if (!item) {
                    update({ 
                      current_address: { 
                        ...data.current_address, 
                        state: null, 
                        state_id: null,
                        state_name: "" 
                      } 
                    });
                  } else {
                    update({ 
                      current_address: { 
                        ...data.current_address, 
                        state: item.id, 
                        state_id: item.id,
                        state_name: item.name 
                      } 
                    });
                  }
                }}
              />
            </div>
          </Col>
        </Row>
      </Card>

      <Row>
        {/* --- PHONES --- */}
        <Col md={6} className="mb-4">
          <Form.Label className="fw-bold small text-uppercase text-muted mb-2">{Vocab.personal_phones[lan]} *</Form.Label>
          {data.phone_numbers.map((phone, i) => {
            const isInvalidPhone = Boolean(phone && !isValidPhoneNumber(phone));
            const isEmpty = !phone || phone.trim() === "";
            return (
              <div key={i} className="mb-3">
                <div className={`shadow-sm rounded border bg-white p-2 ${(isInvalidPhone || (isEmpty && data.phone_numbers.length === 1)) ? 'border-danger' : ''}`}>
                  <Row className="g-0 align-items-center">
                    <Col xs="auto" className="pe-2 border-end">
                      <FaPhoneAlt className={(isInvalidPhone || isEmpty) ? "text-danger small mx-1" : "text-primary small mx-1"}/>
                    </Col>
                    <Col className="px-2">
                      <div className="d-flex align-items-center" style={{ minHeight: '38px' }}>
                        <PhoneWidget value={phone || ""} onChange={(val?: string) => updateArray('phone_numbers', i, null, val || "")} />
                      </div>
                    </Col>
                    {data.phone_numbers.length > 1 && (
                      <Col xs="auto" className="ps-2">
                        <Button variant="link" className="text-danger p-0 m-0" onClick={() => removeRow('phone_numbers', i)}>
                          <FaTrash size={14} />
                        </Button>
                      </Col>
                    )}
                  </Row>
                </div>
                <ErrorMessage show={isInvalidPhone} message={lan === 0 ? "Número inválido" : "Invalid number"} />
              </div>
            );
          })}
          <Button 
            variant="link" 
            className="p-0 text-decoration-none" 
            onClick={() => addRow('phone_numbers', "", 3)} 
            disabled={data.phone_numbers.length >= 3}
          >
            <FaPlus size={10} className="me-1"/> {Vocab.add_phone[lan]}
          </Button>
        </Col>

        {/* --- EMERGENCY CONTACTS --- */}
        <Col md={6}>
          <Form.Label className="fw-bold small text-uppercase text-muted mb-2">{Vocab.emergency_contacts[lan]} *</Form.Label>
          {data.emergency_contacts.map((contact, i) => {
            const isInvalidPhone = Boolean(contact.phone && !isValidPhoneNumber(contact.phone));
            const nameEmpty = isInvalid(contact.name);
            const phoneEmpty = !contact.phone;

            return (
              <Card className={`mb-3 p-3 bg-white border shadow-sm ${(isInvalidPhone || nameEmpty || phoneEmpty) ? 'border-danger' : ''}`} key={i}>
                <Row className="g-2 mb-2 align-items-center">
                  <Col xs={10} md={11}>
                    <Form.Control 
                      className="border-0 bg-light fw-bold" 
                      placeholder={Vocab.name[lan]} 
                      value={contact.name || ""} 
                      isInvalid={nameEmpty} 
                      onChange={(e) => updateArray('emergency_contacts', i, 'name', e.target.value)} 
                    />
                  </Col>
                  {data.emergency_contacts.length > 1 && (
                    <Col xs={2} md={1} className="text-end">
                      <Button variant="link" className="text-danger p-0" onClick={() => removeRow('emergency_contacts', i)}>
                        <FaTrash size={14} />
                      </Button>
                    </Col>
                  )}
                </Row>
                <Row className="g-2">
                  <Col xs={12} md={7}>
                    <div className={`bg-light rounded px-2 py-1 d-flex align-items-center ${phoneEmpty ? 'border border-danger' : ''}`} style={{ minHeight: '38px' }}>
                      <PhoneWidget value={contact.phone || ""} onChange={(val?: string) => updateArray('emergency_contacts', i, 'phone', val || "")} />
                    </div>
                  </Col>
                  <Col xs={12} md={5}>
                    <EnumSelect 
                      className="border-0 bg-light h-100"
                      value={contact.relationship ?? RelationshipsEnum.PARENT} 
                      options={RELATIONSHIPS}
                      isInvalid={!contact.relationship}
                      onChange={(val: any) => updateArray('emergency_contacts', i, 'relationship', Number(val))}
                    />
                  </Col>
                </Row>
                <ErrorMessage show={isInvalidPhone} message={lan === 0 ? "Revisa el número" : "Check number"} />
              </Card>
            );
          })}
          <Button 
            variant="link" 
            className="p-0 text-decoration-none" 
            onClick={() => addRow('emergency_contacts', { name: "", phone: "", relationship: RelationshipsEnum.PARENT }, 5)} 
            disabled={data.emergency_contacts.length >= 5}
          >
            <FaPlus size={10} className="me-1"/> {Vocab.add_contact[lan]}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default StepContact;